import classNames         from 'classnames';
import * as PropTypes     from 'prop-types';
import * as React         from 'react';
import { useTranslation } from 'react-i18next';

const SingleSelect = function(props) {
  const { t } = useTranslation();

  const {
          className,
          containsImage,
          disabled,
          isOpened,
          labelKey    = 'label',
          onChangeCallback,
          onSearch,
          options     = [],
          placeholder = 'label_all',
          required,
          search,
          selected: value,
          valueKey    = 'ID',
        } = props;

  const selectRef = React.useRef(null);

  const [isOpen, setIsOpen] = React.useState(isOpened ?? false);
  const [query, setQuery]   = React.useState('');

  const selected    = React.useMemo(() => {
    return value !== null && options.filter((item) => {
      return item[valueKey] === value;
    }).length > 0
      ? options.find((item) => {
        return item[valueKey] === value;
      })[labelKey]
      : t(placeholder);
  }, [value, options, valueKey, labelKey]);
  const variants    = React.useMemo(() => {
    return options.length > 0
      ? options.filter((item) => {
        return item[labelKey]
          ?.toLowerCase()
          .match(new RegExp(query
              ?.toLowerCase()
              .escapeChars(),
            'i'));
      })
      : [];
  }, [options, labelKey, query]);
  const nullEnabled = React.useMemo(() => {
    return options.findIndex((item) => {
      return item[valueKey] === null;
    }) >= 0;
  }, [options, valueKey]);

  const onClick            = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isOpened) {
      return;
    }

    if (!disabled && Object.keys(options).length > 0) {
      setIsOpen((open) => !open);
    }
  }, [disabled, isOpened, options]);
  const handleClickOutside = React.useCallback((event) => {
    if (isOpened) {
      return;
    }

    if (isOpen && (selectRef.current && !selectRef.current.contains(event.target))) {
      setIsOpen(false);
      setQuery('');
    }
  }, [isOpen, isOpened, selectRef]);
  const handleKeyDown      = React.useCallback((event) => {
    if (isOpened) {
      return;
    }

    if (isOpen && event.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  }, [isOpen, isOpened]);
  const handleChange       = React.useCallback((event, index) => {
    event.preventDefault();
    event.stopPropagation();

    const nullEnabled = options.findIndex((item) => {
      return item[valueKey] === null;
    }) >= 0;
    const newValue    = index !== value ? index : nullEnabled ? void 0 : null;

    onChangeCallback?.(newValue);

    const selectedItem = options.find((item) => {
      return item[valueKey] === newValue;
    });

    setIsOpen(false);
    setQuery(search ? selectedItem?.[labelKey] ?? '' : '');
  }, [isOpened, labelKey, nullEnabled, onChangeCallback, options, search, value, valueKey]);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      className={classNames('select singleSelect', className, { 'open': isOpen })}
      ref={selectRef}
    >
      <span
        className={classNames('title', containsImage, { 'enabled': !disabled })}
        onClick={onClick}
      >
        {search
          ? (
            <input
              className={'search'}
              onChange={(event) => {
                const { value } = event.target;

                setQuery(value);
                onSearch(value);
              }}
              placeholder={selected}
              value={query}
            />
          )
          : selected
        }
        <span className={'ic-arrow-bottom'} />
      </span>
      <div className={classNames('body', { 'enabled': !disabled })}>
        <ul>
          {!required && (
            <li
              className={classNames({ 'current': nullEnabled ? typeof value === 'undefined' : value === null })}
              onClick={(event) => {
                handleChange(event, nullEnabled ? void 0 : null);
              }}
            >{t(placeholder)}</li>
          )}
          {variants.length > 0 && variants.map((item, index) => {
            return (
              <li
                className={classNames({
                  'current':      item[valueKey] === value,
                  'headerLetter': item.isHeader,
                })}
                key={index}
                onClick={(event) => {
                  handleChange(event, item[valueKey]);
                }}
              >{item[labelKey]}</li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

SingleSelect.propTypes = {
  selected:         PropTypes.number,
  options:          PropTypes.array.isRequired,
  required:         PropTypes.bool,
  valueKey:         PropTypes.string,
  labelKey:         PropTypes.string,
  onChangeCallback: PropTypes.func.isRequired,
  search:           PropTypes.bool,
};

export default SingleSelect;